<template>
  <vue-page-transition name="fade-in-down">
    <v-content class="bg-content">
      <div class="wrap">
        <!-- ===== Header ===== -->
        <header class="top">
          <div class="brand">
            <span class="logo">🛠️</span>
            <div>
              <h3>XM | AI</h3>
              <small>Event Planner + Cost + Execution</small>
            </div>
          </div>

          <div class="status">
            <button class="level level-btn" type="button" @click="resetProject(false)">
  ♻️ Reset
</button>
<button class="level level-btn" type="button" @click="resetProject(true)">
  🧼 Reset (Keep Research/Projects)
</button>

            <button class="level level-btn" type="button" @click="openOverlay('archive')">Archive</button>
            <button class="level level-btn" type="button" @click="openOverlay('neural')">Neural Brain</button>

            <!-- Save / Projects -->
            <button class="level level-btn" type="button" @click="saveProject(false, false)">💾 Save</button>
            <button class="level level-btn" type="button" @click="loadLastProject()" :disabled="!hasSavedDraft">↩️ Load Last</button>
            <button class="level level-btn" type="button" @click="openProjects()">📚 Projects</button>

            <!-- Export / Share -->
            <button class="btn ghost" type="button" @click="downloadReportPdf" :disabled="!reportJson">
              📄 Download PDF
            </button>
            <button class="btn ghost" type="button" @click="copyShareLink" :disabled="!canShare">
              🔗 Copy Share Link
            </button>
            <button class="btn ghost" type="button" @click="emailShare" :disabled="!canShare">
              ✉️ Email
            </button>

            <a
              class="btn ghost"
              :class="{ disabled: !research.query }"
              :href="research.query ? `https://www.google.com/search?q=${encodeURIComponent(research.query)}` : '#'"
              target="_blank"
              rel="noopener"
            >Open in Google</a>

            <h5 class="overlay-title">{{ modeLabel }}</h5>
          </div>
        </header>

        <!-- ===== Idle Hero ===== -->
        <div v-if="isIdle" class="idle-hero">
          <GlowingSphere :size="340" :listening="listening" :thinking="thinking" :responding="responding" />

          <div class="live-preview live-preview--avatar" v-if="previewTranscript && listening">
            🎙️ {{ previewTranscript }}
          </div>

          <button
            class="big-mic"
            :class="{ on: listening, arming: isArming && !listening }"
            type="button"
            :title="listening ? 'Stop listening' : (isArming ? 'Arming…' : 'Start speaking')"
            @click.stop="startModeVoice(mode || 'intake')"
          >
            🎙️ {{ listening ? 'Stop' : (isArming ? 'Arming…' : 'Speak') }}
          </button>

          <button class="btn ghost mt-3" type="button" @click="deactivateIdle()">Open App</button>
        </div>

        <!-- ===== Main App ===== -->
        <div v-else>
          <main class="grid">
            <!-- Left modes -->
            <aside class="modes">
              <h3>Modes</h3>

              <button
                v-for="m in modes"
                :key="m.key"
                class="card"
                :class="{ active: mode === m.key, open: mode === m.key }"
                @click="onSelectMode(m.key)"
              >
                <div class="icon">{{ m.icon }}</div>

                <div class="meta">
                  <b>{{ m.title }}</b>
                  <small>{{ m.desc }}</small>
                </div>

                <button class="mini-mic" type="button" title="Start voice for this mode" @click.stop="startModeVoice(m.key)">
                  🎙️
                </button>

                <transition name="card-open">
                  <div v-if="mode === m.key" class="card-open-body">
                    <p class="hint">{{ modeHints[m.key] }}</p>
                    <div class="quick-actions">
                      <button type="button" class="qa" @click.stop="focusForm()">Focus input</button>
                      <button type="button" class="qa" @click.stop="startModeVoice(m.key)">
                        {{ listening ? 'Stop listening' : 'Start listening' }}
                      </button>
                    </div>
                  </div>
                </transition>
              </button>

              <div class="side-actions">
                <button class="btn" type="button" @click="toggleIdle" :title="isIdle ? 'Close Avatar' : 'Avatar'">
                  {{ isIdle ? 'Close Avatar' : 'Avatar' }}
                </button>
              </div>
            </aside>

            <!-- Stage -->
            <section class="stage hero-sphere">
          <GlowingSphere :size="340" :listening="listening" :thinking="thinking" :responding="responding" :class="isIdle ? 'idle' : ''" />

              <button
                class="fab-mic"
                :class="{ on: listening, arming: isArming && !listening }"
                type="button"
                :title="listening ? 'Stop listening' : (isArming ? 'Arming…' : 'Speak')"
                @click.stop="startModeVoice(mode || 'intake')"
              >
                🎙️ <span class="label">{{ listening ? 'Stop' : (isArming ? 'Arming…' : 'Speak') }}</span>
              </button>

              <div class="panel">
                <div v-if="error" class="error">🔴 {{ error }}</div>

                <!-- ===== Chat log (shared) ===== -->
                <div class="chat">
                  <div class="messages" ref="messages">
                    <div v-for="(m, i) in messages" :key="i" :class="['msg', m.role]">
                      <p>{{ m.content }}</p>
                    </div>
                  </div>
                </div>

                <!-- ===== Mode panels ===== -->
                <div class="mode-panels">
                  <!-- INTAKE -->
                  <section v-if="mode === 'intake'" class="panel-section">
                    <h3>Event Intake</h3>

                    <div class="grid2">
                      <label>
                        Event Type
                        <input v-model="event.event_type" placeholder="e.g., Product launch, gala, pop-up..." />
                      </label>

                      <label>
                        Budget Target
                        <input v-model.number="event.budget" type="number" min="0" placeholder="15000" />
                      </label>

                      <label>
                        Project Spending Goal
                        <input v-model.number="event.spending_goal" type="number" min="0" placeholder="12000" />
                      </label>

                      <label>
                        Location
                        <input v-model="event.location" placeholder="City / Venue name" />
                      </label>

                      <label>
                        Event Date
                        <input v-model="event.date" placeholder="YYYY-MM-DD" />
                      </label>

                      <label class="colspan">
                        Key Needs (comma-separated)
                        <input v-model="event.key_needs_text" placeholder="stage backdrop, step-and-repeat, lounge, signage..." />
                      </label>

                      <label class="colspan">
                        Audience / Headcount
                        <input v-model="event.attendees" placeholder="e.g., 120" />
                      </label>

                      <label class="colspan">
                        Venue constraints (load-in, ceiling, union, hours, etc.)
                        <input v-model="event.constraints" placeholder="Dock access, 8ft doors, union labor, 2hr setup window..." />
                      </label>

                      <label class="colspan">
                        Style / Look & Feel
                        <input v-model="event.look_and_feel" placeholder="Modern, premium, black & chrome, LED accents..." />
                      </label>
                    </div>

                    <div class="row2 mt-2">
                      <button class="btn" type="button" @click="aiGenerateRequirementsQuestions">
                        🧠 Ask for Missing Details (AI)
                      </button>
                      <button class="btn ghost" type="button" @click="seedDefaults">
                        ✨ Use Standard Assumptions
                      </button>
                    </div>

                    <hr class="sep" />

                    <h4>Uploads (reference images, floorplans, brand guides)</h4>
                    <div class="row2">
                      <input type="file" multiple accept="image/*" @change="onUploadFiles" />
                      <button class="btn ghost" type="button" @click="clearUploads" :disabled="!uploads.length">Clear</button>
                    </div>

                    <div v-if="uploads.length" class="upload-grid">
                      <figure v-for="(u, i) in uploads" :key="'u-'+i" class="upload-cell">
                        <img :src="u.dataUrl" :alt="u.name" />
                        <figcaption>
                          <small>{{ u.name }}</small>
                          <button class="btn ghost" type="button" @click="removeUpload(i)">Remove</button>
                        </figcaption>
                      </figure>
                    </div>

                    <div class="row2 mt-3">
                      <button class="btn" type="button" @click="onSelectMode('research')">Next: Research 🔎</button>
                      <button class="btn" type="button" @click="onSelectMode('products')">Next: Products 📦</button>
                    </div>
                  </section>

                  <!-- RESEARCH -->
                  <section v-else-if="mode === 'research'" class="panel-section">
                    <h3>Online Research</h3>

                    <div class="research-hud">
                      <span class="badge" :class="{ ok: research.cseLoaded, warn: !research.cseLoaded }">
                        <strong>CSE</strong> {{ research.cseLoaded ? 'Loaded' : 'Loading…' }}
                      </span>
                      <span class="badge plan" v-if="searching">⏳ Searching…</span>
                      <span class="badge ok" v-else>✅ {{ research.results.length }} results</span>
                    </div>

                    <label>Search Query</label>
                    <input
                      ref="researchInput"
                      v-model="research.query"
                      placeholder="Search vendors, materials, rentals, print options..."
                      @keyup.enter="runGoogleSearch"
                    />

                    <div class="row2 research-actions">
                      <button class="btn" :disabled="!research.query" @click="runGoogleSearch">🔎 Search</button>
                      <button class="btn" @click="collectGoogleResults">📥 Collect</button>
                      <button class="btn ghost" :disabled="!research.results.length" @click="summarizeResearch">
                        🧠 Summarize
                      </button>
                      <button class="btn" :disabled="!research.results.length" @click="aiSuggestProductsFromResearch">
                        📦 Suggest Products (AI)
                      </button>
                    </div>

                    <div class="google-cse-wrapper">
                      <div id="cse-search"></div>
                      <div id="cse-results"></div>
                    </div>

                    <div v-if="research.summary" class="summary-box">
                      <h4>AI Summary</h4>
                      <p>{{ research.summary }}</p>
                    </div>

                    <div v-if="research.results.length" class="results">
                      <h4>Collected Results ({{ research.results.length }})</h4>
                      <ul>
                        <li v-for="(r, idx) in research.results" :key="r.id || ('r-'+idx)">
                          <div class="row">
                            <button class="title as-link" type="button" @click="previewResult(r)">
                              {{ r.title }}
                            </button>
                            <span class="host">({{ r.host }})</span>
                            <span class="conf-chip" :class="r.siteType">
                              {{ r.siteType }} • {{ Math.round(r.confidence * 100) }}%
                            </span>
                          </div>
                          <p class="snippet">{{ r.snippet }}</p>
                          <div class="row2 mt-2">
                            <button class="btn ghost" type="button" @click="previewUrl(r.link)">Preview</button>
                            <a class="btn ghost" :href="r.link" target="_blank" rel="noopener">Open</a>
                          </div>
                        </li>
                      </ul>
                    </div>

                    <!-- Website preview frame -->
                    <div class="preview-wrap" v-if="research.previewUrl">
                      <div class="row2" style="justify-content: space-between; align-items:center;">
                        <h4 style="margin:0;">Preview</h4>
                        <div class="row2">
                          <a class="btn ghost" :href="research.previewUrl" target="_blank" rel="noopener">Open</a>
                          <button class="btn ghost" type="button" @click="research.previewUrl = ''">Close</button>
                        </div>
                      </div>

                      <iframe
                        class="site-preview"
                        :src="research.previewUrl"
                        sandbox="allow-forms allow-scripts allow-same-origin allow-popups"
                        referrerpolicy="no-referrer"
                      ></iframe>

                      <div class="muted mt-2">
                        Note: Some sites block iframe previews (X-Frame-Options). If blank, click “Open”.
                      </div>
                    </div>

                    <div class="row2 mt-3">
                      <button class="btn" type="button" @click="onSelectMode('products')">Next: Products 📦</button>
                      <button class="btn ghost" type="button" @click="onSelectMode('intake')">Back: Intake</button>
                    </div>
                  </section>

                  <!-- PRODUCTS -->
                  <section v-else-if="mode === 'products'" class="panel-section">
                    <h3>Products + Costs</h3>
<button class="btn ghost" type="button" @click="aiCostDownPlan" :disabled="!products.length">
  💸 Cost Down Options (AI)
</button>

                    <div class="grid2">
                      <label>
                        Add Product
                        <input v-model="draft.product_name" placeholder="e.g., 10x20 fabric backdrop, LED uplights..." />
                      </label>

                      <label>
                        Purpose
                        <input v-model="draft.purpose" placeholder="Role in event" />
                      </label>

                      <label>
                        Unit Price
                        <input v-model.number="draft.unit_price" type="number" min="0" placeholder="2500" />
                      </label>

                      <label>
                        Qty
                        <input v-model.number="draft.qty" type="number" min="1" />
                      </label>

                      <label>
                        Turnaround / Lead Time
                        <input v-model="draft.turnaround_time" placeholder="e.g., 2 weeks, 5 business days" />
                      </label>

                      <label>
                        Supplier Options (comma-separated)
                        <input v-model="draft.supplier_options_text" placeholder="TBD_LOCAL_FAB_SHOP, TBD_SIGN_PRINTER..." />
                      </label>

                      <label class="colspan">
                        Notes (dimensions, materials, finish)
                        <input v-model="draft.notes" placeholder="Assumed size 10x8, SEG fabric, matte laminate..." />
                      </label>
                    </div>

                    <div class="row2 mt-2">
                      <button class="btn" type="button" @click="addProduct">➕ Add</button>
                      <button class="btn ghost" type="button" @click="aiSuggestProductsFromEvent">
                        🧠 Auto-Fill Starter List (AI)
                      </button>
                      <button class="btn ghost" type="button" @click="seedQuoteFromDraft()">
                        💬 Add Quote From Draft
                      </button>
                    </div>

                    <div v-if="products.length" class="table-wrap mt-3">
                      <table class="tbl">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Purpose</th>
                            <th class="num">Qty</th>
                            <th class="num">Unit</th>
                            <th class="num">Line</th>
                            <th>Lead</th>
                            <th>Vendors</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(p, i) in products" :key="'p-'+i">
                            <td>
                              <b>{{ p.product_name }}</b>
                              <div class="muted">{{ p.notes }}</div>
                            </td>
                            <td>{{ p.purpose }}</td>
                            <td class="num">{{ p.qty }}</td>
                            <td class="num">{{ p.unit_price }}</td>
                            <td class="num">{{ (Number(p.qty) * Number(p.unit_price)) }}</td>
                            <td>{{ p.turnaround_time }}</td>
                            <td class="muted">{{ (p.supplier_options || []).join(', ') }}</td>
                            <td>
                              <button class="btn ghost" type="button" @click="editProduct(i)">Edit</button>
                              <button class="btn ghost" type="button" @click="removeProduct(i)">Remove</button>
                              <button class="btn ghost" type="button" @click="researchForProduct(p)">Find Sources</button>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <div class="totals">
                        <div><b>Products Subtotal:</b> {{ productsSubtotal }}</div>
                        <div><b>Budget Target:</b> {{ event.budget || 0 }}</div>

                        <div><b>Spending Goal:</b> {{ event.spending_goal || 0 }}</div>
                        <div class="goalbar">
                          <div class="goalbar__track">
                            <div class="goalbar__fill" :style="{ width: goalPct + '%' }"></div>
                          </div>
                          <small class="muted">
                            {{ Math.min(100, Math.round(goalPct)) }}% of goal ({{ estimatedTotal }} / {{ event.spending_goal || 0 }})
                          </small>
                        </div>

                        <div>
                          <b>Delta:</b>
                          <span :class="{ ok: budgetDelta <= 0, warn: budgetDelta > 0 }">{{ budgetDelta }}</span>
                        </div>

                        <div v-if="overGoal" class="warn">
                          ⚠️ Over spending goal by {{ Math.round(estimatedTotal - Number(event.spending_goal || 0)) }}
                        </div>
                      </div>
                    </div>

                    <hr class="sep" />

                    <div class="grid2">
                      <label>
                        Labor (estimate)
                        <input v-model.number="costs.labor" type="number" min="0" placeholder="1800" />
                      </label>
                      <label>
                        Logistics / Freight
                        <input v-model.number="costs.logistics" type="number" min="0" placeholder="1200" />
                      </label>
                      <label>
                        Other Fees (permits, insurance, venue)
                        <input v-model.number="costs.fees" type="number" min="0" placeholder="500" />
                      </label>
                      <label>
                        Contingency %
                        <input v-model.number="costs.contingencyPct" type="number" min="0" max="30" />
                      </label>
                    </div>

                    <!-- Quotes (sourcing) -->
                    <hr class="sep" />
                    <h4>Sourcing Quotes (Best Price Tracking)</h4>

                    <div class="grid2">
                      <label>
                        Quote Product
                        <select v-model="quoteDraft.product_name">
                          <option value="">Select product…</option>
                          <option v-for="(p, i) in products" :key="'qp-'+i" :value="p.product_name">{{ p.product_name }}</option>
                        </select>
                      </label>

                      <label>
                        Vendor
                        <input v-model="quoteDraft.vendor" placeholder="Vendor name" />
                      </label>

                      <label class="colspan">
                        URL (vendor page)
                        <input v-model="quoteDraft.url" placeholder="https://…" />
                      </label>

                      <label>
                        Unit Price
                        <input v-model.number="quoteDraft.unit_price" type="number" min="0" />
                      </label>

                      <label>
                        Qty
                        <input v-model.number="quoteDraft.qty" type="number" min="1" />
                      </label>

                      <label>
                        Shipping
                        <input v-model.number="quoteDraft.shipping" type="number" min="0" />
                      </label>

                      <label>
                        Lead Time
                        <input v-model="quoteDraft.lead_time" placeholder="e.g., 7–10 business days" />
                      </label>

                      <label class="colspan">
                        Notes
                        <input v-model="quoteDraft.notes" placeholder="Material, finish, minimums, etc." />
                      </label>
                    </div>

                    <div class="row2 mt-2">
                      <button class="btn" type="button" @click="addQuote()">➕ Add Quote</button>
                      <button class="btn ghost" type="button" @click="clearQuotes" :disabled="!quotes.length">Clear Quotes</button>
                      <button class="btn ghost" type="button" @click="previewUrl(quoteDraft.url)" :disabled="!quoteDraft.url">Preview URL</button>
                    </div>

                    <div v-if="quotes.length" class="table-wrap mt-2">
                      <table class="tbl">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Vendor</th>
                            <th>URL</th>
                            <th class="num">Unit</th>
                            <th class="num">Qty</th>
                            <th class="num">Ship</th>
                            <th>Lead</th>
                            <th class="num">Total</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(q, i) in quotes" :key="'q-'+i">
                            <td>
                              <b>{{ q.product_name }}</b>
                              <div v-if="isBestQuote(q)" class="ok">🏆 Best</div>
                              <div class="muted">{{ q.notes }}</div>
                            </td>
                            <td>{{ q.vendor }}</td>
                            <td>
                              <button class="btn ghost" type="button" @click="previewUrl(q.url)" :disabled="!q.url">Preview</button>
                            </td>
                            <td class="num">{{ q.unit_price }}</td>
                            <td class="num">{{ q.qty }}</td>
                            <td class="num">{{ q.shipping || 0 }}</td>
                            <td>{{ q.lead_time }}</td>
                            <td class="num">{{ quoteTotal(q) }}</td>
                            <td>
                              <button class="btn ghost" type="button" @click="removeQuote(i)">Remove</button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div v-else class="muted mt-2">
                      Add quotes to track best pricing. Use “Preview” to open vendor pages in the dashboard (Research mode).
                    </div>

                    <hr class="sep" />

                    <div class="row2 mt-2">
                      <button class="btn" type="button" @click="aiGenerateFullReport">🧾 Generate Full Plan Report (AI)</button>
                      <button class="btn" type="button" @click="onSelectMode('report')" :disabled="!reportJson">View Report</button>
                    </div>

                    <div class="row2 mt-3">
                      <button class="btn ghost" type="button" @click="onSelectMode('research')">Back: Research</button>
                      <button class="btn" type="button" @click="onSelectMode('render')">Next: Render 🖼️</button>
                    </div>
                  </section>

                  <!-- REPORT -->
                  <section v-else-if="mode === 'report'" class="panel-section">
                    <h3>Detailed Plan Report</h3>

                    <div v-if="!reportJson" class="muted">No report yet. Generate one in Products mode.</div>

                    <div v-else class="report">
                      <div class="row2">
                        <button class="btn ghost" type="button" @click="copyReportJson">Copy JSON</button>
                        <button class="btn ghost" type="button" @click="downloadReportJson">Download JSON</button>
                        <button class="btn" type="button" @click="downloadReportPdf">📄 Download PDF</button>
                        <button class="btn" type="button" @click="onSelectMode('render')">Render Image 🖼️</button>
                      </div>

                      <h4>Reasoning</h4>
                      <pre class="pre">{{ reportJson.reasoning }}</pre>

                      <h4>Products</h4>
                      <div class="table-wrap">
                        <table class="tbl">
                          <thead>
                            <tr>
                              <th>Product</th><th>Purpose</th><th class="num">Price</th><th>Lead</th><th>Suppliers</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(p, i) in (reportJson.event_plan && reportJson.event_plan.products ? reportJson.event_plan.products : [])" :key="'rp-'+i">
                              <td><b>{{ p.product_name }}</b></td>
                              <td>{{ p.purpose }}</td>
                              <td class="num">{{ p.price_estimate }}</td>
                              <td>{{ p.turnaround_time }}</td>
                              <td class="muted">{{ (p.supplier_options || []).join(', ') }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <h4>Cost Breakdown</h4>
                      <pre class="pre">{{ pretty(reportJson.event_plan ? reportJson.event_plan.cost_breakdown : {}) }}</pre>

                      <h4>Timeline</h4>
                      <ul>
                        <li v-for="(t, i) in (reportJson.event_plan && reportJson.event_plan.timeline ? reportJson.event_plan.timeline : [])" :key="'tl-'+i">{{ t }}</li>
                      </ul>

                      <h4>Logistics Notes</h4>
                      <pre class="pre">{{ reportJson.event_plan ? reportJson.event_plan.logistics_notes : '' }}</pre>

                      <h4>Budget Suggestions</h4>
                      <ul>
                        <li v-for="(b, i) in (reportJson.event_plan && reportJson.event_plan.budget_suggestions ? reportJson.event_plan.budget_suggestions : [])" :key="'bs-'+i">{{ b }}</li>
                      </ul>

                      <h4>Conclusion</h4>
                      <pre class="pre">{{ reportJson.conclusion }}</pre>
                    </div>

                    <div class="row2 mt-3">
                      <button class="btn ghost" type="button" @click="onSelectMode('products')">Back: Products</button>
                    </div>
                  </section>

                  <!-- RENDER -->
                  <section v-else-if="mode === 'render'" class="panel-section">
                    <h3>Rendered Concept Image</h3>

                    <div class="muted mb-2">
                      Generates a “final look” render based on your plan (and optionally your uploaded reference images).
                    </div>

                    <label>Render Prompt</label>
                    <input ref="imagePrompt" v-model="render.prompt" placeholder="Describe the final build..." />

                    <div class="row2 mt-2">
                      <label>Size
                        <select v-model="render.size">
                          <option>1024x1024</option>
                          <option>1024x576</option>
                          <option>1920x1080</option>
                        </select>
                      </label>
                      <label>Style
                        <select v-model="render.style">
                          <option>photorealistic</option>
                          <option>illustration</option>
                          <option>cinematic ui</option>
                        </select>
                      </label>
                    </div>

                    <div class="row2 mt-2">
                      <button class="btn" type="button" @click="aiBuildRenderPrompt" :disabled="!event.event_type && !products.length && !reportJson">
                        🧠 Build Prompt From Plan (AI)
                      </button>
                      <button class="btn" type="button" @click="generateRenderImage" :disabled="!render.prompt">
                        🖼️ Generate Render
                      </button>
                      <button class="btn ghost" type="button" @click="clearRenderImages" :disabled="!render.generated.length">
                        🧹 Clear
                      </button>
                    </div>

                    <div class="image-preview" v-if="render.generated.length">
                      <h4>Generated Image{{ render.generated.length > 1 ? 's' : '' }}</h4>
                      <div class="image-grid">
                        <figure v-for="(u, i) in render.generated" :key="'gen-'+i" class="image-cell">
                          <img :src="u" :alt="`Render ${i+1}`" />
                          <figcaption>
                            <button type="button" class="btn" @click="downloadImage(u, i)">⬇️ Download</button>
                            <a :href="u" target="_blank" rel="noopener" class="btn ghost">Open</a>
                          </figcaption>
                        </figure>
                      </div>
                    </div>

                    <div class="row2 mt-3">
                      <button class="btn ghost" type="button" @click="onSelectMode('products')">Back: Products</button>
                      <button class="btn" type="button" @click="onSelectMode('report')" :disabled="!reportJson">View Report</button>
                    </div>
                  </section>
                </div>

                <!-- ===== Shared input controls ===== -->
                <form class="form" @submit.prevent="submitFreeform">
                  <label class="muted">Quick AI command (any mode)</label>
                  <input
                    ref="chatInput"
                    v-model="freeform"
                    placeholder="e.g., 'Build a $15k product launch plan in LA' or 'Suggest cheaper alternatives'"
                    @keyup.enter="submitFreeform"
                  />

                  <div class="input">
                    <button type="button" class="icon" @click="toggleMic" :title="listening ? 'Stop' : 'Speak'">🎙️</button>
                    <button class="icon" type="submit" title="Send">📨</button>
                  </div>

                  <VoiceVisualizer :listening="listening" :responding="responding" :thinking="thinking" />
                </form>

                <!-- Printable area for PDF (offscreen) -->
                <div class="print-area" ref="printArea">
                  <div v-if="reportJson" class="print-card">
                    <h4>Event Planner Report</h4>
                    <p><b>Event:</b> {{ event.event_type || "TBD" }} • {{ event.location || "TBD" }} • {{ event.date || "TBD" }}</p>
                    <p>
                      <b>Budget:</b> {{ event.budget || 0 }} •
                      <b>Spending Goal:</b> {{ event.spending_goal || 0 }} •
                      <b>Estimated Total:</b> {{ estimatedTotal || 0 }}
                    </p>

                    <h3>Products</h3>
                    <ul>
                      <li v-for="(p, i) in products" :key="'pp-'+i">
                        <b>{{ p.product_name }}</b> — Qty {{ p.qty }} × {{ p.unit_price }} ({{ p.turnaround_time }})
                      </li>
                    </ul>

                    <h3>Best Quotes</h3>
                    <ul v-if="quotes.length">
                      <li v-for="(q, i) in quotes" :key="'pq-'+i">
                        <b>{{ q.product_name }}</b> — {{ q.vendor }} — Total {{ quoteTotal(q) }} — {{ q.lead_time }}
                      </li>
                    </ul>
                    <p v-else>No quotes recorded.</p>

                    <h3>AI Plan</h3>
                    <pre class="pre">{{ pretty(reportJson) }}</pre>
                  </div>
                </div>
              </div>
            </section>
          </main>
<hr class="sep" />
<h4>Saved Research</h4>

<div class="row2">
  <button class="btn ghost" type="button" @click="saveResearchSession('manual')">
    Save Current Research
  </button>
  <button class="btn ghost" type="button" @click="clearResearchSessions" :disabled="!research.sessions.length">
    Clear Research
  </button>
</div>

<div v-if="!research.sessions.length" class="muted mt-2">No saved searches yet.</div>

<ul v-else class="projects-list">
  <li v-for="s in research.sessions" :key="s.id" class="projects-item">
    <div>
      <b>{{ s.query || "Untitled search" }}</b>
      <div class="muted">{{ (s.location ? s.location + " • " : "") + new Date(s.updatedAt).toLocaleString() }}</div>
      <div class="muted">{{ (s.results?.length || 0) }} results</div>
    </div>

    <div class="row2">
      <button class="btn" type="button" @click="loadResearchSession(s.id)">Load</button>
      <button class="btn ghost" type="button" @click="deleteResearchSession(s.id)">Delete</button>
    </div>
  </li>
</ul>

          <!-- ===== Overlays ===== -->
          <transition name="overlay-fade">
            <div v-if="showOverlay" class="overlay-mask" @click.self="closeOverlay">
              <div class="overlay-card">
                <header class="overlay-header">
                  <h4>
                    {{
                      overlayKey === 'archive'
                        ? 'Neural Archive'
                        : overlayKey === 'neural'
                          ? 'Neural Brain'
                          : 'Projects'
                    }}
                  </h4>
                  <button class="close" @click="closeOverlay" aria-label="Close overlay">✕</button>
                </header>

                <div class="overlay-body">
                  <!-- Projects view -->
                  <div v-if="overlayKey === 'projects'" class="projects">
                    <h4>Recent Projects</h4>

                    <div class="row2">
                      <button class="btn" type="button" @click="saveProject(true, false)">Save as New Snapshot</button>
                      <button class="btn ghost" type="button" @click="clearAllProjects" :disabled="!projects.length">Clear All</button>
                    </div>

                    <div v-if="!projects.length" class="muted mt-2">No saved projects yet.</div>

                    <ul v-else class="projects-list">
                      <li v-for="p in projects" :key="p.id" class="projects-item">
                        <div>
                          <b>{{ p.title }}</b>
                          <div class="muted">{{ new Date(p.updatedAt).toLocaleString() }}</div>
                        </div>

                        <div class="row2">
                          <button class="btn" type="button" @click="loadProjectById(p.id)">Load</button>
                          <button class="btn ghost" type="button" @click="deleteProject(p.id)">Delete</button>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <!-- Archive/Neural dynamic components -->
                  <keep-alive v-else>
                    <component :is="overlayComp" v-if="overlayComp" />
                  </keep-alive>

                  <div v-if="overlayError" class="overlay-error">⚠️ {{ overlayError }}</div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </v-content>
  </vue-page-transition>
</template>

<script>
import axios from "axios";
import GlowingSphere from "@/components/GlowingSphere.vue";
import VoiceVisualizer from "@/components/VoiceVisualizerNew.vue";
import { getSpeechRecognition, speak } from "@/utils/speech";

// ⚠️ Adjust these imports to match your app filenames
import { AI } from "@/utils/aiNew"; // if yours is "@/utils/ai", change it.
import { scoreAndDedupe, normalizeUrl } from "@/utils/researchUtilsNew";
import { buildEventFabSystemPrompt, buildEventFabUserPayload } from "@/utils/eventFabPrompts";

export default {
  name: "EventPlanner",
  components: { GlowingSphere, VoiceVisualizer },

  data() {
    return {
      error: "",
      searching: false,
      recognition: null,
      mediaStream: null,

      // keys (do not hardcode real keys)
      gptApiKey: null,
      googleApiKey: null,
      cseCx: null,

      // voice
      previewTranscript: "",
      silenceTimer: null,
      autoSubmitDelay: 3000,
      autoSubmitArmed: false,
      lastVoiceHadContent: false,
      isArming: false,
      pendingOpenTimer: null,

      // UI
      isIdle: true,
      showOverlay: false,
      overlayKey: null,
      overlayComp: null,
      overlayError: "",
      steps: [
  { key: "intake", label: "Project intake completed", done: false, doneAt: null },
  { key: "research", label: "Research collected & summarized", done: false, doneAt: null },
  { key: "products", label: "BOM / products scoped", done: false, doneAt: null },
  { key: "quotes", label: "Quotes added / best options selected", done: false, doneAt: null },
  { key: "report", label: "Runbook report generated", done: false, doneAt: null },
  { key: "render", label: "Concept render generated", done: false, doneAt: null }
],

      // persistence
      persist: {
        autosave: true,
        includeUploads: false // NOTE: uploads as dataURLs can overflow localStorage quickly
      },
      projects: [],

      // messages
      messages: [
        { role: "assistant", content: "Tell me the event type, budget, date, location, key needs, and spending goal—then I’ll build the full plan." }
      ],
      freeform: "",

      modes: [
        { key: "intake", title: "Intake", desc: "Requirements + uploads", icon: "🧾" },
        { key: "research", title: "Research", desc: "Google CSE + collect", icon: "🔎" },
        { key: "products", title: "Products", desc: "Scope + pricing + sourcing", icon: "📦" },
        { key: "report", title: "Report", desc: "JSON plan output", icon: "🧠" },
        { key: "render", title: "Render", desc: "Concept image", icon: "🖼️" }
      ],

      modeHints: {
        intake: "Enter event details + upload references. Ask AI for missing questions if needed.",
        research: "Search vendors/materials. Collect results. Summarize. Preview pages. Generate product suggestions.",
        products: "Add/edit products + costs. Add quotes to track best price. Generate the final plan report JSON.",
        report: "Review plan reasoning, itemization, timeline, and logistics notes. Export PDF/JSON.",
        render: "Generate a photorealistic render prompt and create an image."
      },

      // app state
      event: {
        event_type: "",
        budget: 0,
        spending_goal: 0,
        location: "",
        date: "",
        attendees: "",
        key_needs_text: "",
        constraints: "",
        look_and_feel: ""
      },

      uploads: [],

research: {
  query: "",
  results: [],
  cseLoaded: false,
  summary: "",
  previewUrl: "",
  sessions: [],      // ✅ gallery
  activeSessionId: null
},
researchSessionsKey() { return "eventplanner.research:sessions"; },


      products: [],
      draft: {
        product_name: "",
        purpose: "",
        unit_price: 0,
        qty: 1,
        turnaround_time: "",
        supplier_options_text: "",
        notes: ""
      },
      editIndex: null,

      // sourcing/quotes
      quotes: [],
      quoteDraft: {
        product_name: "",
        vendor: "",
        url: "",
        unit_price: 0,
        qty: 1,
        shipping: 0,
        lead_time: "",
        notes: ""
      },

      costs: {
        labor: 0,
        logistics: 0,
        fees: 0,
        contingencyPct: 8
      },

      reportJson: null,

      render: {
        prompt: "",
        size: "1024x1024",
        style: "photorealistic",
        generated: []
      }
    };
  },

  computed: {
    listening() { return this.$store?.state?.listening || false; },
    thinking() { return this.$store?.state?.thinking || false; },
    responding() { return this.$store?.state?.responding || false; },

    mode() { return this.$store?.state?.mode || "intake"; },
    modeLabel() {
      const m = this.modes.find(x => x.key === this.mode);
      return m ? `${m.icon} ${m.title}` : "EventPlanner";
    },

    productsSubtotal() {
      return (this.products || []).reduce((sum, p) => sum + (Number(p.unit_price || 0) * Number(p.qty || 0)), 0);
    },
    contingencyAmount() {
      const base = this.productsSubtotal + Number(this.costs.labor || 0) + Number(this.costs.logistics || 0) + Number(this.costs.fees || 0);
      return Math.round(base * (Number(this.costs.contingencyPct || 0) / 100));
    },
    estimatedTotal() {
      const base = this.productsSubtotal + Number(this.costs.labor || 0) + Number(this.costs.logistics || 0) + Number(this.costs.fees || 0);
      return base + this.contingencyAmount;
    },
    budgetDelta() {
      return (this.estimatedTotal || 0) - (Number(this.event.budget || 0) || 0);
    },

    hasSavedDraft() {
      try { return !!localStorage.getItem("eventplanner.draft:last"); } catch (_) { return false; }
    },
    canShare() {
      return !!(this.event?.event_type || (this.products || []).length || this.reportJson);
    },
    overGoal() {
      const g = Number(this.event.spending_goal || 0);
      return g > 0 && Number(this.estimatedTotal || 0) > g;
    },
    goalPct() {
      const g = Number(this.event.spending_goal || 0);
      if (!g) return 0;
      return (Number(this.estimatedTotal || 0) / g) * 100;
    },
    bestQuotesByProduct() {
      const map = new Map();
      for (const q of (this.quotes || [])) {
        const key = String(q.product_name || "").trim().toLowerCase();
        if (!key) continue;
        const total = (Number(q.unit_price || 0) * Number(q.qty || 1)) + Number(q.shipping || 0);
        const prev = map.get(key);
        if (!prev || total < prev.total) map.set(key, { ...q, total });
      }
      return map;
    }
  },

  watch: {
    showOverlay(v) {
      document.documentElement.style.overflow = v ? "hidden" : "";
      document.body.style.overflow = v ? "hidden" : "";
    },
    mode(val) {
      if (val === "research") {
        this.$nextTick(() => this.loadGoogleCSE());
      }
    },

    // autosave state
    event: { deep: true, handler() { this.queueAutosave(); } },
    products: { deep: true, handler() { this.queueAutosave(); } },
    costs: { deep: true, handler() { this.queueAutosave(); } },
    research: { deep: true, handler() { this.queueAutosave(); } },
    quotes: { deep: true, handler() { this.queueAutosave(); } }
  },

  mounted() {
    this.loadKeys();
    if (!this.$store?.state?.mode) this.$store?.commit?.("SET_MODE", "intake");

    // projects index
    this.loadProjectsIndex();
this.loadResearchSessions();

    // share link (hash) restore takes priority over last draft
// share link (hash) restore takes priority
const restored = this.restoreFromShareLink();

// ✅ default behavior: start fresh on load (do NOT auto-load last draft)
if (!restored) this.resetOnLoad();


    this.recognition = getSpeechRecognition();
    if (this.recognition) {
      this.recognition.interimResults = true;
      this.recognition.continuous = true;

      this.recognition.onstart = () => {
        this.isArming = false;
        this.$store?.dispatch?.("startListening");
        this.autoSubmitArmed = true;
        this.lastVoiceHadContent = false;
        this.resetSilenceTimer();
      };

      this.recognition.onend = () => {
        this.isArming = false;
        this.$store?.dispatch?.("stopListening");
        if (this.autoSubmitArmed && this.lastVoiceHadContent) this.finishVoiceAndSubmit();
        else this.clearSilenceTimer();
        this.previewTranscript = "";
      };

      this.recognition.onerror = (e) => {
        this.isArming = false;
        this.error = `Voice recognition error: ${e.error}`;
        this.clearSilenceTimer();
      };

      this.recognition.onresult = (e) => {
        let finalText = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const seg = e.results[i][0].transcript;
          if (e.results[i].isFinal) finalText += seg;
          else this.previewTranscript = seg;
        }

        if (finalText.trim()) {
          this.previewTranscript = "";
          this.routeVoice(finalText.trim());
          try { if (this.listening) this.recognition.stop(); } catch (e2) { void e2; }
        }

        this.lastVoiceHadContent = true;
        this.resetSilenceTimer();
      };
    }

    // auto-collect CSE DOM changes
    const mountAutoCollector = () => {
      const el = document.getElementById("cse-results");
      if (!el || this.__cseObserver) return;

      this.__cseObserver = new MutationObserver(() => {
        clearTimeout(this.__cseDebounce);
        this.__cseDebounce = setTimeout(() => this.collectGoogleResults(), 180);
      });
      this.__cseObserver.observe(el, { childList: true, subtree: true });
    };
    this.$nextTick(mountAutoCollector);
  },

  beforeDestroy() {
    if (this.__cseObserver) { this.__cseObserver.disconnect(); this.__cseObserver = null; }
    if (this.recognition) {
      try { this.recognition.stop(); } catch (e) { void e; }
      this.recognition = null;
    }
    clearTimeout(this.__autosaveTimer);
  },

  methods: {
    // ======================
    // Keys
    // ======================
    loadKeys() {
      const env = (typeof process !== "undefined" && process.env) ? process.env : {};
      const clean = v => (v ? String(v).trim().replace(/^['"]|['"]$/g, "") : null);

      this.gptApiKey = clean(env.VUE_APP_OPENAI_API_KEY) || clean(env.VUE_APP_OPENAI_KEY) || clean(env.OPENAI_API_KEY) || null;
      this.googleApiKey = clean(env.VUE_APP_GOOGLE_API_KEY) || clean(env.GOOGLE_API_KEY) || null;
      this.cseCx = clean(env.VUE_APP_GOOGLE_CSE_CX) || clean(env.GOOGLE_CSE_CX) || clean(env.CSE_CX) || null;

      try {
        if (this.gptApiKey) localStorage.setItem("openai.key", this.gptApiKey);
        if (this.googleApiKey) localStorage.setItem("google.key", this.googleApiKey);
        if (this.cseCx) localStorage.setItem("google.cx", this.cseCx);
      } catch (e) { void e; }

      AI.setKey(this.gptApiKey);
    },

    // ======================
    // UI helpers
    // ======================
    toggleIdle() { this.isIdle = !this.isIdle; },
    deactivateIdle() { this.isIdle = false; },

    onSelectMode(key) {
      this.$store?.commit?.("SET_MODE", key);
      this.$nextTick(() => this.focusForm());
    },
markStepDone(key) {
  const s = (this.steps || []).find(x => x.key === key);
  if (!s) return;
  s.done = true;
  s.doneAt = Date.now();
  // hard commit after step completion
  this.saveProject(false, true);
},
resetOnLoad() {
  // Keep libraries (projects index + saved research sessions) but reset the working state
  const keepProjects = Array.isArray(this.projects) ? [...this.projects] : [];
  const keepResearchSessions = Array.isArray(this.research?.sessions) ? [...this.research.sessions] : [];

  // Reset working state (do NOT delete localStorage "last" draft)
  this.event = {
    event_type: "",
    budget: 0,
    spending_goal: 0,
    location: "",
    date: "",
    attendees: "",
    key_needs_text: "",
    constraints: "",
    look_and_feel: ""
  };

  this.uploads = [];
  this.products = [];
  this.quotes = [];
  this.costs = { labor: 0, logistics: 0, fees: 0, contingencyPct: 8 };
  this.reportJson = null;

  // research working state reset, keep gallery
  this.research = {
    ...this.research,
    query: "",
    results: [],
    summary: "",
    previewUrl: "",
    activeSessionId: null,
    sessions: keepResearchSessions
  };

  this.render = {
    prompt: "",
    size: "1024x1024",
    style: "photorealistic",
    generated: []
  };

  this.messages = [
    { role: "assistant", content: "New project started. Tell me the event type, budget, date, location, key needs, and spending goal—then I’ll build the full plan." }
  ];

  // Restore indexes to avoid any accidental wipe
  this.projects = keepProjects;

  // Put UI back to intake
  this.$store?.commit?.("SET_MODE", "intake");
  this.isIdle = false; // optional: keep avatar state on load (set false if you want app open)
},
commitStep(note = "") {
  // saves + leaves a breadcrumb in the chat log
  this.saveProject(false, true);
  if (note) {
    this.messages.push({ role: "assistant", content: `✅ Saved: ${note}` });
    this.scrollToBottom();
  }
},
resetProject(keepLibraries = true) {
  const keepProjects = keepLibraries ? (this.projects || []) : [];
  const keepResearchSessions = keepLibraries ? (this.research.sessions || []) : [];

  // wipe state
  this.event = { event_type:"", budget:0, spending_goal:0, location:"", date:"", attendees:"", key_needs_text:"", constraints:"", look_and_feel:"" };
  this.uploads = [];
  this.products = [];
  this.quotes = [];
  this.reportJson = null;
  this.render = { prompt:"", size:"1024x1024", style:"photorealistic", generated:[] };
  this.steps = (this.steps || []).map(s => ({ ...s, done:false, doneAt:null }));

  // reset research working state but optionally keep gallery
  this.research.query = "";
  this.research.results = [];
  this.research.summary = "";
  this.research.previewUrl = "";
  this.research.activeSessionId = null;
  this.research.sessions = keepResearchSessions;

  this.messages = [
    { role: "assistant", content: "New project started. Tell me the event type, budget, date, location, key needs, and spending goal—then I’ll build the full plan." }
  ];


  // keep or clear indexes
  this.projects = keepProjects;
  this.saveProjectsIndex();
  this.saveResearchSessionsIndex();

  this.onSelectMode("intake");
  this.commitStep("Project reset");
},

    focusForm() {
      this.$nextTick(() => {
        let el = null;
        if (this.mode === "research") el = this.$refs.researchInput;
        else if (this.mode === "render") el = this.$refs.imagePrompt;
        else el = this.$refs.chatInput;
        if (el && typeof el.focus === "function") el.focus();
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messages;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
loadResearchSessions() {
  try {
    const raw = localStorage.getItem(this.researchSessionsKey());
    const arr = raw ? JSON.parse(raw) : [];
    this.research.sessions = Array.isArray(arr) ? arr : [];
  } catch (_) {
    this.research.sessions = [];
  }
},

saveResearchSessionsIndex() {
  try {
    localStorage.setItem(this.researchSessionsKey(), JSON.stringify(this.research.sessions || []));
  } catch (_) { /* ignore */ }
},

saveResearchSession(kind = "search") {
  const q = (this.research.query || "").trim();
  const results = (this.research.results || []).slice(0, 40);
  if (!q && !results.length && !this.research.summary) return;

  const id = this.research.activeSessionId || `rs_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const session = {
    id,
    kind,
    query: q,
    location: this.event.location || "",
    createdAt: Date.now(),
    updatedAt: Date.now(),
    results,
    summary: this.research.summary || ""
  };

  const idx = (this.research.sessions || []).findIndex(s => s.id === id);
  if (idx >= 0) this.research.sessions.splice(idx, 1, session);
  else this.research.sessions = [session, ...(this.research.sessions || [])].slice(0, 50);

  this.research.activeSessionId = id;
  this.saveResearchSessionsIndex();
},

loadResearchSession(id) {
  const s = (this.research.sessions || []).find(x => x.id === id);
  if (!s) return;
  this.research.query = s.query || "";
  this.research.results = Array.isArray(s.results) ? s.results : [];
  this.research.summary = s.summary || "";
  this.research.activeSessionId = s.id;
  this.onSelectMode("research");
  this.commitStep(`Loaded research session: "${s.query || "Untitled"}"`);
},

deleteResearchSession(id) {
  this.research.sessions = (this.research.sessions || []).filter(s => s.id !== id);
  if (this.research.activeSessionId === id) this.research.activeSessionId = null;
  this.saveResearchSessionsIndex();
},

clearResearchSessions() {
  this.research.sessions = [];
  this.research.activeSessionId = null;
  try { localStorage.removeItem(this.researchSessionsKey()); } catch (_) { /* ignore */ }
},

    // ======================
    // Uploads
    // ======================
    async onUploadFiles(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      for (const f of files) {
        if (!/^image\//i.test(f.type)) continue;
        const dataUrl = await this.fileToDataUrl(f);
        this.uploads.push({ name: f.name, type: f.type, dataUrl });
      }

      try { e.target.value = ""; } catch (e2) { void e2; }
    },

    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ""));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
    },

    removeUpload(i) { this.uploads.splice(i, 1); },
    clearUploads() { this.uploads = []; },

    // ======================
    // Voice
    // ======================
    startModeVoice(target = "intake", delay = 400) {
      if (!this.recognition) {
        this.error = "Speech recognition is not supported in this browser.";
        return;
      }

      if (this.pendingOpenTimer) { clearTimeout(this.pendingOpenTimer); this.pendingOpenTimer = null; }

      if (this.listening) {
        try { this.recognition.stop(); } catch (e) { void e; }
        this.isArming = false;
        return;
      }

      this.isArming = true;
      this.pendingOpenTimer = setTimeout(() => {
        this.pendingOpenTimer = null;
        this.onSelectMode(target);
        this.toggleMic();
      }, delay);
    },

    clearSilenceTimer() { if (this.silenceTimer) { clearTimeout(this.silenceTimer); this.silenceTimer = null; } },

    resetSilenceTimer() {
      this.clearSilenceTimer();
      if (!this.autoSubmitArmed) return;
      this.silenceTimer = setTimeout(() => this.finishVoiceAndSubmit(), this.autoSubmitDelay);
    },

    async finishVoiceAndSubmit() {
      if (!this.autoSubmitArmed) return;
      this.autoSubmitArmed = false;
      this.clearSilenceTimer();

      try { if (this.listening) this.recognition.stop(); } catch (e) { void e; }
      const t = (this.previewTranscript || "").trim();
      if (t) this.freeform = this.smartAppend(this.freeform, t);
      this.previewTranscript = "";
      if (this.freeform.trim()) await this.submitFreeform();
    },

    smartAppend(base = "", chunk = "") {
      const a = String(base || "").trim();
      const b = String(chunk || "").trim();
      if (!a) return b;
      if (!b) return a;
      if (a.toLowerCase().endsWith(b.toLowerCase())) return a;
      return (a + " " + b).trim();
    },

    routeVoice(text) {
      const s = String(text || "").trim();
      const low = s.toLowerCase();

      const renderMatch = s.match(/^(?:create|make|generate)\s+(?:an?\s+)?(?:image|render|picture)\s*(.*)$/i);
      if (renderMatch) {
        this.onSelectMode("render");
        this.render.prompt = (renderMatch[1] || "").trim() || this.render.prompt;
        return;
      }

      const researchMatch = s.match(/^(?:research|google|browse|search(?:\s+for)?|look\s+up|find)\s+(.*)$/i);
      if (researchMatch) {
        this.onSelectMode("research");
        this.research.query = (researchMatch[1] || "").trim() || this.research.query;
        this.$nextTick(() => this.runGoogleSearch());
        return;
      }

      if (low.startsWith("add product") || low.startsWith("add item")) {
        this.onSelectMode("products");
        this.freeform = this.smartAppend(this.freeform, s);
        this.$nextTick(() => this.submitFreeform());
        return;
      }

      this.freeform = this.smartAppend(this.freeform, s);
      this.$nextTick(() => this.submitFreeform());
    },
async aiCostDownPlan() {
  const system =
    "You are a senior experiential producer + estimator. Propose cost-down options WITHOUT breaking the creative intent. " +
    "Return JSON only (no markdown).";
  const ctx = this.buildAIContext(true);

  const text = await this.aiChat(system,
`Return JSON with:
{
  "savings_moves":[
    {"move":"...", "why":"...", "items_affected":["..."], "est_savings":1234, "tradeoff":"...", "risk":"low|med|high"}
  ],
  "revised_recommendations":{
    "products":[{"product_name":"...", "qty":1, "unit_price":1000, "notes":"..."}],
    "costs":{"labor":0,"logistics":0,"fees":0,"contingencyPct":8}
  }
}

CONTEXT:
${JSON.stringify(ctx, null, 2)}
`);
  const json = this.safeJsonExtract(text, null);
  if (!json) { this.error = "Cost-down AI did not return valid JSON."; return; }

  // Store it on reportJson (or a new field)
  this.reportJson = this.reportJson || {};
  this.reportJson.cost_down_options = json;

  this.messages.push({ role: "assistant", content: "Cost-down options generated and saved into the report." });
  this.scrollToBottom();
  this.saveProject(false, true);
},

    async toggleMic() {
      if (!this.recognition) {
        this.error = "Speech recognition is not supported in this browser.";
        return;
      }

      if (!this.mediaStream) {
        try { this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
        catch (e) { void e; }
      }

      try {
        if (this.listening) {
          this.autoSubmitArmed = false;
          this.clearSilenceTimer();
          this.recognition.stop();
          this.previewTranscript = "";
        } else {
          this.error = "";
          this.previewTranscript = "";
          this.autoSubmitArmed = true;
          this.lastVoiceHadContent = false;
          this.recognition.start();
          this.resetSilenceTimer();
        }
      } catch (_) {
        this.error = "Failed to start speech recognition.";
      }
    },

    // ======================
    // Google CSE
    // ======================
    async loadGoogleCSE() {
      if (this.research.cseLoaded) return;

      if (!this.cseCx) {
        this.error = "Google CSE CX not configured. Set VUE_APP_GOOGLE_CSE_CX or localStorage google.cx.";
        return;
      }

      await this.$nextTick();
      const waitForDivs = async () => {
        for (let i = 0; i < 25; i++) {
          const a = document.getElementById("cse-search");
          const b = document.getElementById("cse-results");
          if (a && b) return true;
          await new Promise(r => setTimeout(r, 40));
        }
        return false;
      };

      const ok = await waitForDivs();
      if (!ok) {
        this.error = "CSE containers not found (#cse-search / #cse-results).";
        return;
      }

      window.__gcse = window.__gcse || { parsetags: "explicit" };

      if (!document.querySelector('script[data-cse="1"]')) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = `https://cse.google.com/cse.js?cx=${encodeURIComponent(this.cseCx)}`;
          s.async = true;
          s.dataset.cse = "1";
          s.onload = resolve;
          s.onerror = () => reject(new Error("CSE load failed"));
          document.head.appendChild(s);
        });
      }

      for (let i = 0; i < 40; i++) {
        if (window.google?.search?.cse?.element) break;
        await new Promise(r => setTimeout(r, 50));
      }
      if (!window.google?.search?.cse?.element) {
        this.error = "Google CSE loaded, but google.search.cse.element is not available.";
        return;
      }

      document.getElementById("cse-search").innerHTML = "";
      document.getElementById("cse-results").innerHTML = "";

      try {
        const el = window.google.search.cse.element;

        el.render({
          div: "cse-search",
          tag: "searchbox-only",
          attributes: { gname: "eventfab", enableAutoComplete: true }
        });

        el.render({
          div: "cse-results",
          tag: "searchresults-only",
          attributes: { gname: "eventfab", linkTarget: "_blank" }
        });

        this.research.cseLoaded = true;
      } catch (e) {
        console.error("CSE render error:", e);
        this.error = "Failed to render Google CSE (see console for details).";
      }
    },

    runCseEmbedded(q) {
      try {
        // IMPORTANT: must match gname used in render(): "eventfab"
        const el = window.google?.search?.cse?.element?.getElement("eventfab");
        if (el && typeof el.execute === "function") { el.execute(q); return true; }
      } catch (e) { void e; }
      return false;
    },

    async runGoogleSearch() {
      const q = (this.research.query || "").trim();
      if (!q) return;

      this.error = "";
      await this.loadGoogleCSE();
      this.runCseEmbedded(q);

      const key = this.googleApiKey || (() => {
        try { return localStorage.getItem("google.key"); } catch (_) { return null; }
      })();
      const cx = this.cseCx || (() => {
        try { return localStorage.getItem("google.cx"); } catch (_) { return null; }
      })();

      if (!key || !cx) return;

      this.searching = true;
      try {
        const res = await axios.get("https://www.googleapis.com/customsearch/v1", {
          params: { key, cx, q, num: 8 },
          timeout: 25000
        });

        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        const now = Date.now();

        const raw = items.map((i, idx) => {
          const link = normalizeUrl(i.link || "");
          const title = (i.title || i.htmlTitle || "").replace(/<[^>]+>/g, "");
          const snippet = (i.snippet || i.htmlSnippet || "").replace(/<[^>]+>/g, "").trim();

          let host = "";
          try { host = new URL(link).hostname.replace(/^www\./, ""); } catch (e) { void e; }

          const siteType = this.siteTypeForHost(host);
          const confidence = this.scoreConfidence({ snippet, siteType, rank: idx });

          return {
            id: `g-${now}-${idx}-${(i.cacheId || host || link).toString().slice(0, 24)}`,
            title, link, snippet, host, siteType, confidence
          };
        });

        this.research.results = scoreAndDedupe([...(this.research.results || []), ...raw]);
      } catch (e) {
        this.error = e?.message || "Google API search failed (embedded results still available).";
} finally {
  this.searching = false;
  this.saveResearchSession("search");     // (new; see below)
  this.commitStep(`Research search saved: "${q}"`);
}
    },

    collectGoogleResults() {
      const container = document.querySelector("#cse-results");
      if (!container) return;

      const nodes = container.querySelectorAll(".gsc-webResult.gsc-result, .gsc-result");
      const raw = [];

      nodes.forEach((n, i) => {
        if (n.closest(".gsc-adBlock") || n.classList.contains("gsc-adBlock")) return;

        const linkEl = n.querySelector("a.gs-title") || n.querySelector(".gs-title a") || n.querySelector(".gs-title");
        const title = (linkEl?.textContent || "").trim();
        const href = (linkEl?.href || "").trim();
        const link = normalizeUrl(href);
        const snippet = (n.querySelector(".gs-snippet")?.textContent || "").trim();

        if (title && link) {
          let host = "";
          try { host = new URL(link).hostname.replace(/^www\./, ""); } catch (e) { void e; }
          const siteType = this.siteTypeForHost(host);
          const confidence = this.scoreConfidence({ snippet, siteType, rank: i });
          raw.push({ id: `cse-${i}-${link}`.slice(0, 120), title, link, snippet, host, siteType, confidence });
        }
      });

      this.research.results = scoreAndDedupe([...(this.research.results || []), ...raw]);
    },

    previewResult(r) {
      if (!r?.link) return;
      this.research.previewUrl = r.link;
    },

    previewUrl(url) {
      if (!url) return;
      this.research.previewUrl = url;
      this.onSelectMode("research");
    },

    siteTypeForHost(host = "") {
      const h = String(host || "").toLowerCase();
      if (!h) return "generic";
      if (/\.(edu|gov|mil)$/.test(h)) return "institutional";
      if (/\b(arxiv|acm|ieee|springer|nature|science|sciencedirect)\b/.test(h)) return "paper";
      if (/\b(wikipedia|wiktionary|wikibooks)\b/.test(h)) return "reference";
      if (/\b(github|gitlab|bitbucket)\b/.test(h)) return "code";
      if (/\b(medium|substack|dev\.to)\b/.test(h)) return "blog";
      if (/\b(youtube|vimeo)\b/.test(h)) return "video";
      return "generic";
    },

    scoreConfidence({ snippet = "", siteType = "generic", rank = 0 } = {}) {
      let s = Math.min(1, (snippet || "").length / 220);
      const bump = { institutional: 0.25, paper: 0.2, reference: 0.15, code: 0.1, blog: -0.05, video: -0.05, generic: 0 };
      s += bump[siteType] ?? 0;
      s += Math.max(0, 0.18 - rank * 0.02);
      return Math.max(0.05, Math.min(0.99, s));
    },

    // ======================
    // AI actions
    // ======================
    buildAIContext(includeResearch = false) {
      const key_needs = (this.event.key_needs_text || "").split(",").map(x => x.trim()).filter(Boolean);
      const uploads = (this.uploads || []).slice(0, 6).map(u => ({ name: u.name, type: u.type }));

      const ctx = {
        event_type: this.event.event_type || "",
        budget: Number(this.event.budget || 0),
        spending_goal: Number(this.event.spending_goal || 0),
        key_needs,
        location: this.event.location || "",
        date: this.event.date || "",
        attendees: this.event.attendees || "",
        constraints: this.event.constraints || "",
        look_and_feel: this.event.look_and_feel || "",
        uploads,
        products: (this.products || []).map(p => ({
          product_name: p.product_name,
          purpose: p.purpose,
          qty: p.qty,
          unit_price: p.unit_price,
          turnaround_time: p.turnaround_time,
          supplier_options: p.supplier_options,
          notes: p.notes
        })),
        quotes: (this.quotes || []).slice(0, 20),
        cost_inputs: { ...this.costs },
        totals: {
          products_subtotal: this.productsSubtotal,
          estimated_total: this.estimatedTotal,
          delta: this.budgetDelta
        }
      };

      if (includeResearch) {
        ctx.research = {
          query: this.research.query,
          results: (this.research.results || []).slice(0, 12).map(r => ({
            title: r.title, link: r.link, host: r.host, snippet: r.snippet
          })),
          summary: this.research.summary || ""
        };
      }

      return ctx;
    },

    async aiChat(system, user) {
  this.error = "";
  this.$store?.dispatch?.("startThinking");
  this.$store?.dispatch?.("startResponding");
  try {
    const res = await AI.chatWithSystem(system, user);
    return res?.text || "";
  } catch (e) {
    this.error = e?.message || "AI request failed.";
    return "";
  } finally {
    this.$store?.dispatch?.("stopThinking");
    this.$store?.dispatch?.("stopResponding");
  }
},

    async aiGenerateRequirementsQuestions() {
      const system = "You are an event producer. Ask only the missing critical questions needed to estimate fabrication, rentals, labor, logistics. Keep it concise.";
      const user = JSON.stringify(this.buildAIContext(), null, 2);
      const out = await this.aiChat(system, `Based on this event intake, list: Confirmed Inputs, Missing Critical Inputs, and up to 10 targeted questions.\n\nINPUT:\n${user}`);

      if (out) {
        this.messages.push({ role: "assistant", content: out });
        this.scrollToBottom();
      }
    },

    splitCSV(v) {
      if (!v) return [];
      if (Array.isArray(v)) return v;
      return String(v).split(",").map(x => x.trim()).filter(Boolean);
    },

    async aiSuggestProductsFromEvent() {
      const system = "You are a fabrication estimator. Propose a starter list of 6–10 products/services with estimated costs and lead times that fit the budget and spending goal. Use placeholders for vendors.";
      const user = JSON.stringify(this.buildAIContext(), null, 2);

      const text = await this.aiChat(system, `Return JSON array only (no markdown) with items: product_name, purpose, unit_price, qty, turnaround_time, supplier_options, notes.\n\nINPUT:\n${user}`);
      const arr = this.safeJsonExtract(text, []);
      if (Array.isArray(arr) && arr.length) {
        this.products = arr.map(x => ({
          product_name: x.product_name || "TBD",
          purpose: x.purpose || "",
          unit_price: Number(x.unit_price || x.price_estimate || 0),
          qty: Number(x.qty || 1),
          turnaround_time: x.turnaround_time || "",
          supplier_options: Array.isArray(x.supplier_options) ? x.supplier_options : this.splitCSV(x.supplier_options),
          notes: x.notes || ""
        }));
        this.messages.push({ role: "assistant", content: "I added a starter product list. Review and adjust pricing/qty, then generate the full report." });
        this.scrollToBottom();
      }
    },

    async aiSuggestProductsFromResearch() {
      if (!this.research.results.length) return;

      const system = "You are a procurement + fabrication estimator. Use the web research sources to suggest realistic products/vendors/pricing ranges and lead times. Prefer best value that fits spending goal.";
      const user = JSON.stringify(this.buildAIContext(true), null, 2);

      const text = await this.aiChat(system, `Return JSON array only (no markdown) with items: product_name, purpose, unit_price, qty, turnaround_time, supplier_options, notes.\n\nINPUT:\n${user}`);
      const arr = this.safeJsonExtract(text, []);
      if (Array.isArray(arr) && arr.length) {
        const existing = new Set((this.products || []).map(p => (p.product_name || "").toLowerCase()));
        for (const x of arr) {
          const name = String(x.product_name || "").trim();
          if (!name) continue;
          if (existing.has(name.toLowerCase())) continue;

          this.products.push({
            product_name: name,
            purpose: x.purpose || "",
            unit_price: Number(x.unit_price || x.price_estimate || 0),
            qty: Number(x.qty || 1),
            turnaround_time: x.turnaround_time || "",
            supplier_options: Array.isArray(x.supplier_options) ? x.supplier_options : this.splitCSV(x.supplier_options),
            notes: x.notes || ""
          });
          existing.add(name.toLowerCase());
        }
        this.messages.push({ role: "assistant", content: "Added product suggestions from research sources. Review totals, then generate the report." });
        this.scrollToBottom();
        this.onSelectMode("products");
      }
    },

    async aiGenerateFullReport() {
      const system = buildEventFabSystemPrompt();
      const payload = buildEventFabUserPayload({
        event: this.event,
        products: this.products,
        costs: this.costs,
        totals: {
          products_subtotal: this.productsSubtotal,
          estimated_total: this.estimatedTotal,
          budget_target: Number(this.event.budget || 0),
          spending_goal: Number(this.event.spending_goal || 0),
          delta: this.budgetDelta
        },
        research: this.research,
        uploads: this.uploads,
        quotes: this.quotes
      });

      const text = await this.aiChat(system, payload);
      const parsed = this.safeJsonExtract(text, null);

      if (!parsed || !parsed.event_plan) {
        this.error = "AI did not return valid JSON for the report. Try again.";
        return;
      }

      this.reportJson = parsed;
      this.messages.push({ role: "assistant", content: "Report generated. Open Report mode to review and refine." });
      this.scrollToBottom();
      this.onSelectMode("report");
    },

    async aiBuildRenderPrompt() {
      const system = "You create a single image-generation prompt for a realistic event render based on the plan. Output ONE line only. No JSON.";
      const user = JSON.stringify(this.buildAIContext(true), null, 2);

      const prompt = await this.aiChat(system,
        `Write one concise image prompt for a photorealistic render. Include: camera angle, lighting, materials, scale cues (people), and the key scenic elements.\n\nPLAN:\n${user}`
      );

      this.render.prompt = String(prompt || "").trim().split("\n").filter(Boolean)[0] || "";
      if (this.render.prompt) {
        this.messages.push({ role: "assistant", content: "Render prompt generated. Click “Generate Render” to create the image." });
        this.scrollToBottom();
      }
    },

    async generateRenderImage() {
      if (!this.render.prompt.trim()) return;

      this.$store?.dispatch?.("startThinking");
      try {
        const result = await AI.image(this.render.prompt.trim(), {
          size: this.render.size,
          style: this.render.style
        });
        if (Array.isArray(result?.imageUrls)) this.render.generated = result.imageUrls;
        this.messages.push({ role: "assistant", content: "Render generated. You can download or open it above." });
        this.scrollToBottom();
      } catch (e) {
        this.error = e?.message || "Image generation failed.";
      } finally {
        this.$store?.dispatch?.("stopResponding");
      }
    },

    clearRenderImages() { this.render.generated = []; },

    downloadImage(url, i = 0) {
      try {
        const a = document.createElement("a");
        a.href = url;
        a.download = `eventplanner-render-${i + 1}.png`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) { void e; }
    },

    async summarizeResearch() {
      if (!this.research.results.length) return;

      const system = "You summarize web research into actionable event planning notes: key takeaways, pricing ranges, lead times, and recommended vendors.";
      const user = [
        `Event context: ${this.event.event_type || "TBD"} in ${this.event.location || "TBD"} on ${this.event.date || "TBD"}`,
        `Budget: ${this.event.budget || 0} / Spending Goal: ${this.event.spending_goal || 0}`,
        `Summarize the sources below into: (1) key insights, (2) price/lead time notes, (3) suggested vendors (title + link).`,
        ...this.research.results.slice(0, 10).map((r, i) => `${i + 1}. ${r.title}\n${r.link}\n${r.snippet}`)
      ].join("\n\n");

      const out = await this.aiChat(system, user);
      this.research.summary = out;
    },

    // ======================
    // Products CRUD
    // ======================
    addProduct() {
      if (!this.draft.product_name.trim()) return;

      const item = {
        product_name: this.draft.product_name.trim(),
        purpose: this.draft.purpose.trim(),
        unit_price: Number(this.draft.unit_price || 0),
        qty: Number(this.draft.qty || 1),
        turnaround_time: this.draft.turnaround_time.trim(),
        supplier_options: this.splitCSV(this.draft.supplier_options_text),
        notes: this.draft.notes.trim()
      };

      if (this.editIndex !== null) {
        this.products.splice(this.editIndex, 1, item);
        this.editIndex = null;
      } else {
        this.products.push(item);
      }

      this.resetDraft();
    },

    editProduct(i) {
      const p = this.products[i];
      if (!p) return;
      this.editIndex = i;
      this.draft.product_name = p.product_name || "";
      this.draft.purpose = p.purpose || "";
      this.draft.unit_price = Number(p.unit_price || 0);
      this.draft.qty = Number(p.qty || 1);
      this.draft.turnaround_time = p.turnaround_time || "";
      this.draft.supplier_options_text = (p.supplier_options || []).join(", ");
      this.draft.notes = p.notes || "";
    },

    removeProduct(i) { this.products.splice(i, 1); },

    resetDraft() {
      this.draft = { product_name: "", purpose: "", unit_price: 0, qty: 1, turnaround_time: "", supplier_options_text: "", notes: "" };
    },

    researchForProduct(p) {
      const q = `${p?.product_name || ""} ${this.event.location || ""} price vendor lead time`.trim();
      if (!q) return;
      this.research.query = q;
      this.onSelectMode("research");
      this.$nextTick(() => this.runGoogleSearch());
    },

    // ======================
    // Quotes (Sourcing)
    // ======================
    quoteTotal(q) {
      const unit = Number(q.unit_price || 0);
      const qty = Number(q.qty || 1);
      const ship = Number(q.shipping || 0);
      return (unit * qty) + ship;
    },

    isBestQuote(q) {
      const key = String(q.product_name || "").trim().toLowerCase();
      const best = this.bestQuotesByProduct.get(key);
      if (!best) return false;
      const total = this.quoteTotal(q);
      return Math.abs(total - Number(best.total || 0)) < 0.0001;
    },

    seedQuoteFromDraft() {
      const name = String(this.draft.product_name || "").trim();
      if (!name) {
        this.messages.push({ role: "assistant", content: "Type a product name in the draft field first, then add a quote." });
        this.scrollToBottom();
        return;
      }
      this.quotes.push({
        product_name: name,
        vendor: "",
        url: "",
        unit_price: Number(this.draft.unit_price || 0),
        qty: Number(this.draft.qty || 1),
        shipping: 0,
        lead_time: this.draft.turnaround_time || "",
        notes: "Quote draft",
        createdAt: Date.now()
      });

      this.quoteDraft.product_name = name;
      this.quoteDraft.unit_price = Number(this.draft.unit_price || 0);
      this.quoteDraft.qty = Number(this.draft.qty || 1);
      this.quoteDraft.lead_time = this.draft.turnaround_time || "";
    },

    addQuote() {
      const pn = String(this.quoteDraft.product_name || "").trim();
      if (!pn) {
        this.error = "Select a product for the quote.";
        return;
      }

      this.quotes.push({
        product_name: pn,
        vendor: String(this.quoteDraft.vendor || "").trim(),
        url: String(this.quoteDraft.url || "").trim(),
        unit_price: Number(this.quoteDraft.unit_price || 0),
        qty: Number(this.quoteDraft.qty || 1),
        shipping: Number(this.quoteDraft.shipping || 0),
        lead_time: String(this.quoteDraft.lead_time || "").trim(),
        notes: String(this.quoteDraft.notes || "").trim(),
        createdAt: Date.now()
      });

      this.quoteDraft = {
        product_name: pn,
        vendor: "",
        url: "",
        unit_price: 0,
        qty: 1,
        shipping: 0,
        lead_time: "",
        notes: ""
      };
    },

    removeQuote(i) { this.quotes.splice(i, 1); },
    clearQuotes() { this.quotes = []; },

    // ======================
    // Report utilities
    // ======================
    pretty(obj) {
      try { return JSON.stringify(obj, null, 2); }
      catch (_) { return String(obj); }
    },

    copyReportJson() {
      try { navigator.clipboard.writeText(JSON.stringify(this.reportJson, null, 2)); }
      catch (e) { void e; }
    },

    downloadReportJson() {
      const blob = new Blob([JSON.stringify(this.reportJson, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "eventplanner-plan.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },

    safeJsonExtract(text, fallback) {
      const s = String(text || "").trim();
      try { return JSON.parse(s); } catch (e) { void e; }

      const i = s.indexOf("{");
      const j = s.lastIndexOf("}");
      if (i >= 0 && j > i) {
        try { return JSON.parse(s.slice(i, j + 1)); } catch (e) { void e; }
      }

      const a = s.indexOf("[");
      const b = s.lastIndexOf("]");
      if (a >= 0 && b > a) {
        try { return JSON.parse(s.slice(a, b + 1)); } catch (e) { void e; }
      }

      return fallback;
    },

    // ======================
    // PDF export (best-effort: jsPDF/html2canvas if installed; otherwise Print->Save as PDF)
    // ======================
    async downloadReportPdf() {
      if (!this.reportJson) return;

      const el = this.$refs.printArea;
      if (!el) {
        window.print();
        return;
      }

      try {
        const jspdfMod = await import(/* webpackChunkName: "jspdf" */ "jspdf");
        const h2cMod = await import(/* webpackChunkName: "html2canvas" */ "html2canvas");

        const jsPDF =
          jspdfMod.jsPDF ||
          jspdfMod.default?.jsPDF ||
          jspdfMod.default;

        const html2canvas =
          h2cMod.default || h2cMod;

        const canvas = await html2canvas(el, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "pt", "a4");
        const w = pdf.internal.pageSize.getWidth();
        const h = (canvas.height * w) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, w, h);
        pdf.save("eventplanner-report.pdf");
        return;
      } catch (_) {
        this.messages.push({ role: "assistant", content: "PDF libs not installed — opening print dialog. Choose “Save as PDF”." });
        this.scrollToBottom();
        window.print();
      }
    },

    // ======================
    // Share link + email
    // ======================
    copyShareLink() {
      try {
        const snap = this.makeSnapshot();
        const payload = btoa(unescape(encodeURIComponent(JSON.stringify(snap))));
        const url = `${window.location.origin}${window.location.pathname}#eventplanner=${payload}`;
        navigator.clipboard.writeText(url);
        this.messages.push({ role: "assistant", content: "Share link copied to clipboard." });
        this.scrollToBottom();
      } catch (e) {
        this.error = "Failed to create share link (too large). Disable uploads persistence or reduce stored state.";
      }
    },

    restoreFromShareLink() {
      try {
        const h = window.location.hash || "";
        const m = h.match(/eventplanner=([^&]+)/);
        if (!m) return false;
        const json = decodeURIComponent(escape(atob(m[1])));
        const snap = JSON.parse(json);
        this.applySnapshot({ id: "shared", ...snap });
        return true;
      } catch (_) {
        return false;
      }
    },

    emailShare() {
      try {
        const snap = this.makeSnapshot();
        const payload = btoa(unescape(encodeURIComponent(JSON.stringify(snap))));
        const link = `${window.location.origin}${window.location.pathname}#eventplanner=${payload}`;

        const subject = encodeURIComponent(`EventPlanner Report: ${this.event.event_type || "Event"}`);
        const body = encodeURIComponent([
          `Event: ${this.event.event_type || "TBD"}`,
          `Location/Date: ${this.event.location || "TBD"} / ${this.event.date || "TBD"}`,
          `Budget: ${this.event.budget || 0}`,
          `Spending Goal: ${this.event.spending_goal || 0}`,
          `Estimated Total: ${this.estimatedTotal || 0}`,
          ``,
          `Share link (opens the dashboard with this plan loaded):`,
          link,
          ``,
          `If the link is too long, export PDF/JSON and attach manually.`
        ].join("\n"));

        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      } catch (_) {
        this.error = "Email share failed.";
      }
    },

    // ======================
    // Persistence (Local Save / Load)
    // ======================
    storageKey(id) { return `eventplanner.project:${id}`; },

    makeSnapshot() {
      const base = {
        version: 1,
        updatedAt: Date.now(),
        title: this.event?.event_type
          ? `${this.event.event_type}${this.event.date ? " • " + this.event.date : ""}`
          : `Untitled • ${new Date().toLocaleDateString()}`,
        state: {
          event: { ...this.event },
          products: Array.isArray(this.products) ? [...this.products] : [],
          costs: { ...this.costs },
          research: {
            query: this.research.query,
            results: (this.research.results || []).slice(0, 40),
            summary: this.research.summary,
            previewUrl: this.research.previewUrl || ""
          },
          quotes: Array.isArray(this.quotes) ? [...this.quotes] : [],
          reportJson: this.reportJson,
          render: { prompt: this.render.prompt, size: this.render.size, style: this.render.style },
          messages: (this.messages || []).slice(-40)
        }
      };

      if (this.persist.includeUploads) base.state.uploads = this.uploads;

      return base;
    },

    applySnapshot(snap) {
      const st = snap?.state || {};
      if (st.event) this.event = { ...this.event, ...st.event };
      if (Array.isArray(st.products)) this.products = st.products;
      if (st.costs) this.costs = { ...this.costs, ...st.costs };

      if (st.research) {
        this.research = {
          ...this.research,
          query: st.research.query || "",
          results: Array.isArray(st.research.results) ? st.research.results : [],
          summary: st.research.summary || "",
          cseLoaded: this.research.cseLoaded,
          previewUrl: st.research.previewUrl || ""
        };
      }

      if (Array.isArray(st.quotes)) this.quotes = st.quotes;
      if (st.reportJson) this.reportJson = st.reportJson;
      if (st.render) this.render = { ...this.render, ...st.render };
      if (Array.isArray(st.messages)) this.messages = st.messages;

      if (this.persist.includeUploads && Array.isArray(st.uploads)) this.uploads = st.uploads;

      this.messages.push({ role: "assistant", content: "Loaded saved project state." });
      this.scrollToBottom();
    },

    loadProjectsIndex() {
      try {
        const raw = localStorage.getItem("eventplanner.projects:index");
        this.projects = raw ? JSON.parse(raw) : [];
      } catch (_) { this.projects = []; }
    },

    saveProjectsIndex() {
      try {
        localStorage.setItem("eventplanner.projects:index", JSON.stringify(this.projects || []));
      } catch (_) { /* ignore */ }
    },

    saveProject(asNew = false, silent = false) {
      const snap = this.makeSnapshot();
      const id = asNew ? `p_${Date.now()}_${Math.random().toString(16).slice(2)}` : "last";

      try {
        localStorage.setItem(this.storageKey(id), JSON.stringify({ id, ...snap }));
        localStorage.setItem("eventplanner.draft:last", JSON.stringify({ id, ...snap }));
      } catch (e) {
        this.error = "Save failed (localStorage full). Turn off uploads persistence or reduce stored data.";
        return;
      }

      const entry = { id, title: snap.title, updatedAt: snap.updatedAt };
      const idx = (this.projects || []).findIndex(x => x.id === id);
      if (idx >= 0) this.projects.splice(idx, 1, entry);
      else this.projects = [entry, ...(this.projects || [])].slice(0, 25);

      this.saveProjectsIndex();

      if (!silent) {
        this.messages.push({ role: "assistant", content: asNew ? "Saved snapshot locally." : "Saved locally." });
        this.scrollToBottom();
      }
    },

    loadLastProject() {
      try {
        const raw = localStorage.getItem("eventplanner.draft:last");
        if (!raw) return;
        const snap = JSON.parse(raw);
        this.applySnapshot(snap);
      } catch (_) { /* ignore */ }
    },

    loadProjectById(id) {
      try {
        const raw = localStorage.getItem(this.storageKey(id));
        if (!raw) return;
        const snap = JSON.parse(raw);
        this.applySnapshot(snap);
      } catch (_) { /* ignore */ }
    },

    deleteProject(id) {
      try { localStorage.removeItem(this.storageKey(id)); } catch (_) { /* ignore */ }
      this.projects = (this.projects || []).filter(x => x.id !== id);
      this.saveProjectsIndex();
    },

    clearAllProjects() {
      const list = this.projects || [];
      for (const p of list) {
        try { localStorage.removeItem(this.storageKey(p.id)); } catch (_) { /* ignore */ }
      }
      try { localStorage.removeItem("eventplanner.draft:last"); } catch (_) { /* ignore */ }
      this.projects = [];
      this.saveProjectsIndex();
    },

    queueAutosave() {
      if (!this.persist.autosave) return;
      clearTimeout(this.__autosaveTimer);
      this.__autosaveTimer = setTimeout(() => {
        try { this.saveProject(false, true); } catch (_) { /* ignore */ }
      }, 700);
    },

    openProjects() {
      this.overlayKey = "projects";
      this.overlayComp = null;
      this.overlayError = "";
      this.showOverlay = true;
    },

    // ======================
    // Freeform chat (shared)
    // ======================
    async submitFreeform() {
      const text = (this.freeform || "").trim();
      if (!text) return;

      this.messages.push({ role: "user", content: text });
      this.scrollToBottom();
      this.freeform = "";

      const system = buildEventFabSystemPrompt();
      const ctx = this.buildAIContext(true);
      const user = `User message:\n${text}\n\nContext:\n${JSON.stringify(ctx, null, 2)}\n\nIf you provide a plan, keep it aligned with budget and spending goal.`;

      const out = await this.aiChat(system, user);
      if (out) {
        this.messages.push({ role: "assistant", content: out });
        this.scrollToBottom();
        speak(String(out).replace(/https?:\/\/\S+/g, ""), null, 1, 1);
      }
    },

    // ======================
    // Overlays (archive/neural)
    // ======================
    async openOverlay(key) {
      this.overlayKey = key;
      this.overlayComp = null;
      this.overlayError = "";
      this.showOverlay = true;

      if (key === "projects") return;

      try {
        let mod = null;
        if (key === "archive") {
          try { mod = (await import(/* webpackChunkName: "archive-overlay" */ "@/views/Archive.vue")).default; }
          catch (_) { mod = (await import(/* webpackChunkName: "archive-overlay" */ "@/components/Archive.vue")).default; }
        } else if (key === "neural") {
          try { mod = (await import(/* webpackChunkName: "neural-overlay" */ "@/views/NeuralNew.vue")).default; }
          catch (_) { mod = (await import(/* webpackChunkName: "neural-overlay" */ "@/components/NeuralNew.vue")).default; }
        }
        this.overlayComp = mod;
      } catch (e) {
        this.overlayError = e?.message || "Failed to load overlay.";
      }
    },

    closeOverlay() {
      this.showOverlay = false;
      this.overlayKey = null;
      this.overlayComp = null;
    },

    seedDefaults() {
      if (!this.event.event_type) this.event.event_type = "Product launch event";
      if (!this.event.budget) this.event.budget = 15000;
      if (!this.event.spending_goal) this.event.spending_goal = 12000;
      if (!this.event.key_needs_text) this.event.key_needs_text = "stage backdrop, on-site branding, lounge seating, lighting accents";
      if (!this.costs.contingencyPct) this.costs.contingencyPct = 8;

      this.messages.push({ role: "assistant", content: "Seeded standard assumptions. Adjust details, then research vendors, add products, and track sourcing quotes." });
      this.scrollToBottom();
    }
  }
};
</script>

<style scoped>
/* If you already have Code 8 CSS in a global file, remove this block and keep class names. */
.bg-content { min-height: 100vh; }
.wrap { padding: 18px 18px 40px; max-width: 1320px; margin: 0 auto; }
.top { display:flex; align-items:center; justify-content:space-between; gap: 12px; margin-bottom: 14px; }
.brand { display:flex; align-items:center; gap: 10px; }
.brand .logo { font-size: 22px; }
.status { display:flex; align-items:center; gap: 10px; flex-wrap: wrap; justify-content:flex-end; }
.overlay-title { margin: 0; opacity: .9; }

.grid { display:grid; grid-template-columns: 300px 1fr; gap: 14px; }
@media (max-width: 1100px) { .grid { grid-template-columns: 1fr; } }

.modes { padding: 12px; border-radius: 14px; background: rgba(12,22,27,.6); border: 1px solid rgba(255,255,255,.08); }
.modes h3 { margin: 4px 0 12px; }
.card { width:100%; text-align:left; border-radius: 14px; padding: 12px; margin-bottom: 10px; background: rgba(0,0,0,.25); border: 1px solid rgba(255,255,255,.08); display:flex; gap: 10px; align-items:flex-start; position: relative; }
.card.active { border-color: rgba(120, 204, 226, .6); box-shadow: 0 0 0 1px rgba(120,204,226,.25); }
.icon { font-size: 20px; width: 30px; }
.meta b { display:block; }
.meta small { opacity: .8; }
.mini-mic { position:absolute; right: 10px; top: 10px; border: 0; background: transparent; cursor: pointer; }

.stage { border-radius: 16px; background: rgba(12,22,27,.6); border: 1px solid rgba(255,255,255,.08); padding: 14px; position: relative; overflow: hidden; }
.panel { margin-top: 10px; padding: 14px; border-radius: 14px; background: rgba(0,0,0,.22); border: 1px solid rgba(255,255,255,.08); }

.error { background: rgba(255, 112, 67, .15); border: 1px solid rgba(255,112,67,.35); padding: 10px; border-radius: 10px; margin-bottom: 10px; }

.chat { max-height: 240px; overflow: auto; border-radius: 12px; border: 1px solid rgba(255,255,255,.08); }
.messages { padding: 10px; }
.msg { margin: 0 0 10px; }
.msg.user p { background: rgba(120, 204, 226, .12); border: 1px solid rgba(120, 204, 226, .25); }
.msg.assistant p { background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.08); }
.msg p { margin: 0; padding: 10px; border-radius: 12px; }

.panel-section { margin-top: 12px; }
.grid2 { display:grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.grid2 label { display:flex; flex-direction: column; gap: 6px; }
.grid2 input, .grid2 select, .form input { border-radius: 10px; border: 1px solid rgba(255,255,255,.1); background: rgba(0,0,0,.25); padding: 10px; color: #fff; }
.colspan { grid-column: 1 / -1; }
.row2 { display:flex; gap: 10px; flex-wrap: wrap; }
.mt-2 { margin-top: 10px; }
.mt-3 { margin-top: 14px; }
.sep { border: 0; height: 1px; background: rgba(255,255,255,.08); margin: 14px 0; }

.btn { border-radius: 999px; padding: 9px 12px; border: 1px solid rgba(255,255,255,.14); background: rgba(120,204,226,.18); color: #fff; cursor: pointer; }
.btn.ghost { background: transparent; }
.btn.disabled, .btn:disabled { opacity: .5; cursor: not-allowed; }
.level-btn { border-radius: 999px; padding: 8px 12px; border: 1px solid rgba(255,255,255,.14); background: rgba(0,0,0,.25); color: #fff; cursor: pointer; }
.level-btn.active { border-color: rgba(120,204,226,.6); box-shadow: 0 0 0 2px rgba(120,204,226,.15); }

.fab-mic { position:absolute; top: 14px; right: 14px; border-radius: 999px; padding: 10px 12px; border: 1px solid rgba(255,255,255,.14); background: rgba(0,0,0,.25); color: #fff; cursor: pointer; }
.fab-mic.on { border-color: rgba(124,179,66,.7); box-shadow: 0 0 0 2px rgba(124,179,66,.15); }
.fab-mic.arming { opacity: .8; }

.idle-hero { display:flex; flex-direction: column; align-items:center; justify-content:center; padding: 30px 14px; border-radius: 16px; background: rgba(12,22,27,.6); border: 1px solid rgba(255,255,255,.08); }
.big-mic { margin-top: 10px; border-radius: 999px; padding: 12px 16px; border: 1px solid rgba(255,255,255,.14); background: rgba(0,0,0,.25); color: #fff; cursor: pointer; }
.big-mic.on { border-color: rgba(124,179,66,.7); }
.live-preview { margin-top: 10px; padding: 10px 12px; border-radius: 12px; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.08); }

.form { margin-top: 14px; position: relative; }
.form .input { position: absolute; right: 10px; bottom: 12px; display:flex; gap: 8px; }
.form .icon { border-radius: 10px; border: 1px solid rgba(255,255,255,.14); background: rgba(0,0,0,.25); color: #fff; padding: 8px 10px; cursor: pointer; }
.muted { opacity: .8; }

.table-wrap { overflow:auto; border: 1px solid rgba(255,255,255,.08); border-radius: 12px; }
.tbl { width:100%; border-collapse: collapse; min-width: 760px; }
.tbl th, .tbl td { padding: 10px; border-bottom: 1px solid rgba(255,255,255,.07); vertical-align: top; }
.tbl th { text-align:left; opacity: .85; }
.num { text-align:right; }
.totals { display:flex; gap: 14px; flex-wrap: wrap; padding: 12px; }
.ok { color: #7CB342; }
.warn { color: #FF7043; }

.upload-grid, .image-grid { display:grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 10px; }
@media (max-width: 900px) { .upload-grid, .image-grid { grid-template-columns: repeat(2, 1fr); } }
.upload-cell, .image-cell { border-radius: 12px; overflow:hidden; border: 1px solid rgba(255,255,255,.08); background: rgba(0,0,0,.25); }
.upload-cell img, .image-cell img { width:100%; height: 150px; object-fit: cover; display:block; }
.upload-cell figcaption, .image-cell figcaption { padding: 10px; display:flex; align-items:center; justify-content:space-between; gap: 10px; }

.overlay-mask { position: fixed; inset: 0; background: rgba(0,0,0,.55); display:flex; align-items:center; justify-content:center; padding: 18px; z-index: 50; }
.overlay-card { width: min(980px, 100%); max-height: 85vh; overflow:auto; border-radius: 16px; background: rgba(12,22,27,.95); border: 1px solid rgba(255,255,255,.1); }
.overlay-header { display:flex; align-items:center; justify-content:space-between; padding: 12px 14px; border-bottom: 1px solid rgba(255,255,255,.08); }
.overlay-body { padding: 14px; }
.close { border:0; background: transparent; color: #fff; font-size: 18px; cursor:pointer; }

.badge { display:inline-flex; gap: 8px; align-items:center; padding: 6px 10px; border-radius: 999px; border: 1px solid rgba(255,255,255,.12); background: rgba(0,0,0,.25); }
.badge.plan { opacity: .9; }
.research-hud { display:flex; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.conf-chip { margin-left: 10px; padding: 3px 8px; border-radius: 999px; border: 1px solid rgba(255,255,255,.12); opacity: .9; }
</style>
